import React, { Component, Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import Lenis from "lenis";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
// Code-split: every route except home loads on demand (SEO + initial bundle weight)
const ServicePage = React.lazy(() => import("./pages/ServicePage"));
const About = React.lazy(() => import("./pages/About"));
const Industries = React.lazy(() => import("./pages/Industries"));
const Process = React.lazy(() => import("./pages/Process"));
const Quote = React.lazy(() => import("./pages/Quote"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Terms = React.lazy(() => import("./pages/Terms"));
const Services = React.lazy(() => import("./pages/Services"));
const Pricing = React.lazy(() => import("./pages/Pricing"));
const LocationPage = React.lazy(() => import("./pages/LocationPage"));
const ServiceAreas = React.lazy(() => import("./pages/ServiceAreas"));
const StatePage = React.lazy(() => import("./pages/StatePage"));
const CountyPage = React.lazy(() => import("./pages/CountyPage"));
const StateServicePage = React.lazy(() => import("./pages/StateServicePage"));
const FocusedLandingPage = React.lazy(() => import("./pages/FocusedLandingPage"));
const IntlHubPage = React.lazy(() => import("./pages/IntlHubPage"));
const IntlCityPage = React.lazy(() => import("./pages/IntlCityPage"));
const MontrealFrPage = React.lazy(() => import("./pages/IntlCityPage").then((m) => ({ default: m.MontrealFrPage })));
const IntlRegionPage = React.lazy(() => import("./pages/IntlRegionPage"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const Admin = React.lazy(() => import("./pages/Admin"));
import { track } from "./lib/track";
import ChatWidget from "./components/ChatWidget";

const RouteLoader = () => (
  <div className="min-h-[60vh] bg-navy-deep flex items-center justify-center" data-testid="route-loader">
    <span className="h-2.5 w-2.5 rotate-45 bg-aqua animate-pulse" aria-hidden="true" />
  </div>
);

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("APP RENDER ERROR:", error?.message, error?.stack, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-navy px-6 text-center">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-aqua uppercase mb-4">Something went wrong</p>
            <h1 className="font-display text-3xl font-bold text-white mb-6">This section failed to load</h1>
            <Link to="/" className="inline-flex bg-aqua text-navy font-bold px-6 py-3 rounded-md hover:bg-aqua-deep transition-colors">
              Return to Home
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const t = setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 350);
      return () => clearTimeout(t);
    }
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  useEffect(() => {
    const t = setTimeout(() => track(pathname), 800);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

const API = process.env.REACT_APP_BACKEND_URL;

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    fetch(`${API}/api/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sid }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("auth failed");
        return r.json();
      })
      .then((user) => navigate("/admin", { state: { user }, replace: true }))
      .catch(() => navigate("/admin?auth=failed", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // OAuth callback: exchange session_id FIRST (render-time hash check prevents race conditions)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/land-survey-cad-drafting" element={<ServicePage />} />
              <Route path="/topographic-survey-drafting" element={<ServicePage />} />
              <Route path="/boundary-survey-drafting" element={<ServicePage />} />
              <Route path="/alta-nsps-survey-drafting" element={<ServicePage />} />
              <Route path="/as-built-survey-drafting" element={<ServicePage />} />
              <Route path="/gis-mapping-services" element={<ServicePage />} />
              <Route path="/gis-data-conversion" element={<ServicePage />} />
              <Route path="/services" element={<Services />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/mortgage-location-survey-drafting" element={<ServicePage />} />
              <Route path="/subdivision-plat-drafting" element={<ServicePage />} />
              <Route path="/easement-survey-drafting" element={<ServicePage />} />
              <Route path="/construction-staking-drafting" element={<ServicePage />} />
              <Route path="/utility-survey-mapping" element={<ServicePage />} />
              <Route path="/route-right-of-way-drafting" element={<ServicePage />} />
              <Route path="/drone-point-cloud-drafting" element={<ServicePage />} />
              <Route path="/fema-elevation-certificate-drafting" element={<ServicePage />} />
              <Route path="/cadastral-mapping-services" element={<ServicePage />} />
              <Route path="/plot-plan-drafting" element={<ServicePage />} />
              <Route path="/grading-earthwork-drafting" element={<ServicePage />} />
              <Route path="/road-highway-survey-drafting" element={<ServicePage />} />
              <Route path="/pdf-to-cad-conversion" element={<ServicePage />} />
              <Route path="/point-cloud-to-cad-conversion" element={<ServicePage />} />
              <Route path="/survey-data-processing" element={<ServicePage />} />
              <Route path="/land-survey-cad-drafting-usa" element={<LocationPage />} />
              <Route path="/land-survey-cad-drafting-uk" element={<LocationPage />} />
              <Route path="/land-survey-cad-drafting-australia" element={<LocationPage />} />
              <Route path="/land-survey-cad-drafting-canada" element={<LocationPage />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/service-areas" element={<ServiceAreas />} />
              <Route path="/service-areas/:slug" element={<StatePage />} />
              <Route path="/service-areas/:slug/county/:county" element={<CountyPage />} />
              <Route path="/service-areas/:slug/:tail" element={<StateServicePage />} />
              <Route path="/survey-cad-outsourcing" element={<FocusedLandingPage />} />
              <Route path="/lidar-point-cloud-to-cad" element={<FocusedLandingPage />} />
              <Route path="/survey-data-conversion" element={<FocusedLandingPage />} />
              <Route path="/civil-3d-survey-drafting" element={<FocusedLandingPage />} />
              {/* International market hubs (Phase 4) */}
              <Route path="/usa" element={<IntlHubPage countrySlug="usa" />} />
              <Route path="/canada" element={<IntlHubPage countrySlug="canada" />} />
              <Route path="/uk" element={<IntlHubPage countrySlug="uk" />} />
              <Route path="/australia" element={<IntlHubPage countrySlug="australia" />} />
              <Route path="/canada/montreal/fr" element={<MontrealFrPage />} />
              <Route path="/:country/city/:city" element={<IntlCityPage />} />
              <Route path="/:country/region/:region" element={<IntlRegionPage />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/our-process" element={<Process />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/request-a-quote" element={<Quote />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/terms-and-conditions" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
          </main>
          <Footer />
          <ChatWidget />
        </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SmoothScroll />
        <ScrollManager />
        <AppRouter />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
